import type { DialogueId, FlagId, NpcId, RoomId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { createRng, hash32 } from './rng';
import type { HandlerEnv } from './placement/handlers';

/** After the first visit, a neighbour drops by on about 30% of days (content-schemas §14.11). */
const DAILY_VISIT_PERCENT = 30;

/** Set once the player has talked to a neighbour visiting Home: from then on visits are the daily 30%. */
export const visitSeenFlag = (npcId: NpcId): FlagId => `flag_visit_seen_${npcId}`;

/**
 * Pipeline step g (content-schemas §14.11): a visit earned before meeting the neighbour waits in
 * `pendingVisits` and becomes active as soon as they have met.
 */
export function visitsSystem(
  draft: GameSave,
  _events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  for (const [npcId, n] of Object.entries(draft.npcs) as [NpcId, GameSave['npcs'][NpcId]][]) {
    if (n.pendingVisits.length === 0) continue;
    const def = env.content.npcs.get(npcId);
    if (!def || !draft.flags[def.metFlag]) continue;
    for (const room of n.pendingVisits) if (!n.activeVisits.includes(room)) n.activeVisits.push(room);
    n.pendingVisits = [];
  }
  return [];
}

/**
 * Who is visiting this room today. The first visit always happens (the next time the player is there);
 * after that it is a seeded 30% per day, so reloading never changes the answer. Cosmetic only.
 */
export function visitorsToday(content: ContentRegistry, s: GameSave, roomId: RoomId): NpcId[] {
  const out: NpcId[] = [];
  for (const [npcId, n] of Object.entries(s.npcs) as [NpcId, GameSave['npcs'][NpcId]][]) {
    if (!n.activeVisits.includes(roomId) || !content.npcs.has(npcId)) continue;
    const first = !s.flags[visitSeenFlag(npcId)];
    if (first || hash32(s.rngSeed, 'visit', npcId, s.clock.lastSeenDayKey) % 100 < DAILY_VISIT_PERCENT)
      out.push(npcId);
  }
  return out;
}

/** The scene for a Home visit: the first-visit dialogue once, then the short visit lines in turn. */
export function pickVisitDialogue(content: ContentRegistry, s: GameSave, npcId: NpcId): DialogueId | null {
  const first: DialogueId = `dlg_${npcId}_visit_home_first`;
  // Keyed on the visit flag, not on "seen": skipping the first scene does not replay it.
  if (content.dialogues.has(first) && !s.flags[visitSeenFlag(npcId)]) return first;
  const prefix = `dlg_${npcId}_visit_home_`;
  let pool = [...content.dialogues.keys()].filter(
    (id): id is DialogueId => id.startsWith(prefix) && id !== first,
  );
  if (pool.length === 0) return null;
  const last = s.npcs[npcId]?.lastDialogueId;
  if (pool.length > 1) pool = pool.filter((id) => id !== last);
  const rng = createRng(hash32(s.rngSeed, 'visitTalk', npcId, s.npcs[npcId]?.talkCount ?? 0));
  return rng.pick(pool);
}
